// Whether the plan on a case still reflects the template. One definition, used by the approve
// button for both its gate and its wording. No Vue reactivity and no API access.
import type { SyncTemplate, Template } from '@/domain/template/template';
import { templatesAreEqual } from '@/views/3d-planner/template/templateComparison';
import {
    type ManualTemplateState,
    syncTemplate,
} from '@/views/3d-planner/template/manualTemplateState';

/**
 * The template the case's most recent plan was made from, or null when it has no plan.
 *
 * A user plan is always newer than the Formus plan, so it wins whenever both exist. The Formus
 * plan is made from the automated template, so that template is its own baseline - which is why
 * only the user plan's template has to be fetched.
 */
export function planBaselineTemplate(
    userPlanTemplate: Template | null,
    autoTemplate: Template | null,
): Template | null {
    return userPlanTemplate ?? (autoTemplate?.plan ? autoTemplate : null);
}

/**
 * Whether the plan on this case no longer reflects the template as it now stands.
 *
 * The baseline is the plan's own template, never the automated one: a plan is out of date when the
 * case has moved away from *it*. With no plan there is nothing for the case to match, so a case
 * that has never been planned is always out of date - which is what keeps the button live on it.
 *
 * Compared by value rather than by template version, because reverting an edit writes a new
 * version holding the values the plan was made from, and that is not a change to approve. Only
 * the comparable fields are asked for, so a caller need not carry a whole `Template`.
 */
export function planIsOutOfDate(
    template: ManualTemplateState | null,
    planTemplate: SyncTemplate | null,
): boolean {
    if (!template) {
        return false;
    }

    if (!planTemplate) {
        return true;
    }

    return !templatesAreEqual(syncTemplate(template), planTemplate);
}
